/* eslint-disable no-fallthrough */
<template>
  <b-col class="m-auto">
    <slot name="logo"></slot>
    <h4 class="text-muted pb-4">{{ labels.title }}</h4>

    <b-form @submit.prevent="createAccount" ref="form">
      <CustomInput
        name="companyName"
        v-model="form.companyName"
        :title="labels.textWarning"
        :label="labels.companyName"
        :labelWrong="labels.companyNameWrong"
      />

      <b-form-group
        class="ml-3 mr-3 mt-0 mb-4 pb-2"
        :label="labels.employeeNumberLabel"
      >
        <b-form-radio-group
          class="ml-1"
          stacked
          id="employeeNumberRadio"
          name="employeeNumber"
          v-model="form.employeeNumber"
          :options="employeeNumber.options"
        ></b-form-radio-group>
      </b-form-group>

      <CustomInput
        name="firstName"
        v-model="form.profile.firstname"
        :title="labels.textWarning"
        :label="labels.firstName"
        :labelWrong="labels.firstNameWrong"
      />

      <CustomInput
        name="lastName"
        v-model="form.profile.lastname"
        :title="labels.textWarning"
        :label="labels.lastName"
        :labelWrong="labels.lastNameWrong"
      />

      <CustomEmail
        inputId="email"
        :title="labels.emailWarning"
        :isValid="emailIsValid"
        :label="emailLabel"
        @update="({ value }) => (form.email = value)"
      />

      <CustomInput
        name="phoneNumber"
        type="tel"
        :hasMask="true"
        v-model="form.phoneNumber"
        :title="labels.phoneWarning"
        :label="labels.phoneNumber"
        :labelWrong="labels.phoneNumberWrong"
      />

      <b-row v-if="message" align-h="center">
        <span
          id="exception-message"
          class="text-secondary error-message pb-1"
          >{{ message }}</span
        >
      </b-row>

      <small class="font-weight-normal">
        {{ labels.partOne }}
        <strong>"{{ labels.subscribe }}"</strong>
        {{ labels.partTwo }}
        <a :href="labels.link.privacy" target="_blank">{{
          labels.partThree
        }}</a>
        {{ labels.partFour }}
        <a :href="labels.link.terms" target="_blank">{{ labels.partFive }}</a
        >.
      </small>

      <Recaptcha ref="recaptcha" @verify="recaptchaVerify">
        <b-button
          id="btn-register"
          @keyup.enter="submit"
          :disabled="submitInProgress"
          type="submit"
          variant="quartenary"
          class="col-12 mt-2 text-white"
        >
          <i
            class="mdi mdi-spin pr-2"
            :class="{ 'mdi-loading': submitInProgress }"
          ></i>
          {{ labels.subscribe }}
        </b-button>
      </Recaptcha>
    </b-form>

    <b-row align-h="center" class="mt-3 mb-5">
      <b-col>
        <b-link :to="{ name: 'login.auth' }">{{ labels.returnToLogin }}</b-link>
      </b-col>
    </b-row>
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import CustomEmail from '@/components/Shared/Form/CustomEmail.vue'
import Recaptcha from '@/components/Shared/Recaptcha.vue'
import { PLANS } from '@/service/user'
import { register } from '@/service/account'
import { isInBlacklist, isEmail } from '@/helpers/validator'
import unconfirmedValidation from '@/helpers/unconfirmedValidation'

export default {
  components: {
    CustomInput,
    CustomEmail,
    Recaptcha,
  },

  data() {
    return {
      submitInProgress: false,
      message: null,
      form: {
        email: undefined,
        profile: {
          firstname: '',
          lastname: '',
        },
        employeeNumber: { minRange: 3, maxRange: 11 },
        companyName: '',
        phoneNumber: '',
        googleRecaptcha: '',
      },
    }
  },

  computed: {
    ...mapGetters(['resources']),

    employeeNumber() {
      return {
        options: [
          {
            text: this.labels.employeeNumberOption1,
            value: { minRange: 3, maxRange: 11 },
          },
          {
            text: this.labels.employeeNumberOption2,
            value: { minRange: 12, maxRange: 200 },
          },
          {
            text: this.labels.employeeNumberOption3,
            value: { minRange: 201, maxRange: 1000 },
          },
          {
            text: this.labels.employeeNumberOption4,
            value: { minRange: 1000, maxRange: 9999 },
          },
        ],
      }
    },

    labels: function() {
      return Object.assign(
        {},
        this.resources.general.errors,
        this.resources.general.input,
        this.resources.general.buttons,
        this.resources.general.termsAndPrivacy,
        this.resources.page.login.new
      )
    },

    emailLabel() {
      const email = (this.form.email || '').trim()

      if (isInBlacklist(email)) {
        return this.labels.emailBlackList
      }

      return email && !isEmail(email)
        ? this.labels.emailWrong
        : this.labels.businessEmail
    },

    emailIsValid() {
      const email = (this.form.email || '').trim()
      return !email ? undefined : isEmail(email) && !isInBlacklist(email)
    },
  },

  methods: {
    recaptchaVerify(value) {
      this.form.googleRecaptcha = value
      this._doCreateAccount()
    },

    createAccount() {
      if (this.submitInProgress) return false

      const isInvalidForm = () => this.$refs.form.checkValidity() === false
      if (isInvalidForm()) {
        this.$refs.form.reportValidity()
        return false
      }

      this.$refs.recaptcha.execute()
    },

    async _doCreateAccount() {
      if (this.submitInProgress) return false

      this.message = null
      this.submitInProgress = true

      const isInvalidForm = () => this.$refs.form.checkValidity() === false

      try {
        if (isInvalidForm()) throw new Error('INVALID_FORM')
        await register(this.form)
        this.onRegisterSuccess()
      } catch (e) {
        this.validateError(e)
      }
      this.submitInProgress = false
    },

    onRegisterSuccess() {
      const createdAt = new Date().toString()

      this.$root.$emit('track:registerUser', {
        email: this.form.email,
        company: this.form.companyName,
        firstName: this.form.profile.firstname,
        lastName: this.form.profile.lastname,
        tenant: 'N/A',
        role: 'admin',
        plan: PLANS.BASIC,
        phone: this.form.phoneNumber,
        colaboradores: this.form.employeeNumber,
        createdAt,
      })

      this.$router.push({ name: 'login.new.success' })
    },

    validateError(error) {
      switch (true) {
        case error.message === 'INVALID_FORM':
          this.$refs.form.reportValidity()

        // no-fallthrough
        case error.message === 'REGISTER_ERROR':
          error.message = this.labels.response500

        // eslint-disable-next-line no-fallthrough
        case unconfirmedValidation(this, error):

        // no-fallthrough
        default:
          this.$refs.recaptcha.resetRecaptcha()
          this.message = error.message || this.labels.response500
          this.$root.$emit('error', {
            context: 'Register [UnknowError]',
            error,
          })
      }
    },
  },
}
</script>
<style lang="scss">
.text-muted {
  font-size: 1.4rem;
}
</style>
